import { handleRefreshToken, signIn, getMe, verifyMFA, clearUserDataCache } from "@/shared/api"
import { clearSessionActivity, markSessionActivity } from "@/shared/sessionActivity"
import { ResponseType, isMFARequired, MFARequiredResponse } from "@/types/SignInResponseType"
import { UserType } from "@/types/UserType"
import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { immer } from "zustand/middleware/immer"
import "../utils/immerPatch"
import { useFeatureAccessStore } from "./featureAccess"
import { clearMaestroPanelSession, markMaestroLoginWelcome } from "@/components/ai-assistant/maestroSession"
import { clearStoredCompany, resetActiveCompanyToUser } from "@/shared/activeCompany"

interface ImpersonationInfo {
  isImpersonating: boolean;
  originalAdminToken: string;
  adminUserId: string;
  adminEmail: string;
  targetUserId: string;
  targetEmail: string;
  reason: string;
  startedAt: Date;
}

interface AuthState {
  loggedIn: boolean;
  user: UserType | null;
  mfaRequired: boolean;
  mfaChallenge: MFARequiredResponse | null;
  impersonation: ImpersonationInfo | null;
  signIn: (email: string, password: string) => Promise<{ mfaRequired: boolean; mfaChallenge?: MFARequiredResponse }>;
  verifyMFA: (token: string) => Promise<void>;
  refresh: () => Promise<void>;
  initializeUser: () => Promise<void>;
  logout: () => void;
  clearMFA: () => void;
  startImpersonation: (impersonationData: Omit<ImpersonationInfo, 'originalAdminToken'>, accessToken: string, refreshToken: string) => void;
  stopImpersonation: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    immer((set) => ({
      loggedIn: false,
      user: null,
      mfaRequired: false,
      mfaChallenge: null,
      impersonation: null,
      signIn: async (email: string, password: string) => {
        try {
          const response = await signIn(email, password);
          
          // Check if MFA is required
          if (isMFARequired(response)) {
            console.log('[AuthStore] MFA required, setting MFA state');
            set((state) => {
              state.mfaRequired = true;
              state.mfaChallenge = response;
              state.loggedIn = false;
              state.user = null;
            });
            return { mfaRequired: true, mfaChallenge: response };
          }
          
          // Successful login without MFA
          if (response && 'accessToken' in response && response.accessToken) {
            clearUserDataCache();
            localStorage.setItem('accessToken', response.accessToken);
            localStorage.setItem('refreshToken', response.refreshToken);
            resetActiveCompanyToUser(response.user);
            markMaestroLoginWelcome();
            markSessionActivity();
            set((state) => {
              state.loggedIn = true;
              state.user = response.user;
              state.mfaRequired = false;
              state.mfaChallenge = null;
            });
            
            // SECURITY: Use feature access from login response (already validated server-side)
            if (response.user.role !== 'SUPER_ADMIN' && response.featureAccess) {
              console.log('[AuthStore] ✅ Loading pre-validated feature access from login response');
              useFeatureAccessStore.getState().setFeatureAccess(
                response.featureAccess.features, 
                response.featureAccess.featuresMap
              );
            } else if (response.user.role !== 'SUPER_ADMIN') {
              // Fallback: Load feature access if not included in response (backward compatibility)
              console.log('[AuthStore] ⚠️ Feature access not in login response, loading separately');
              useFeatureAccessStore.getState().loadFeatureAccess();
            }
          }
          
          return { mfaRequired: false };
        } catch (error) {
          console.error('Sign in failed:', error);
          throw error;
        }
      },
      verifyMFA: async (token: string) => {
        try {
          const state = useAuthStore.getState();
          if (!state.mfaChallenge) {
            throw new Error('No MFA challenge found');
          }
          
          const response = await verifyMFA(state.mfaChallenge.userId, token, state.mfaChallenge.method);
          
          // MFA verification should return a successful login response
          if (!isMFARequired(response) && 'accessToken' in response && response.accessToken) {
            clearUserDataCache();
            localStorage.setItem('accessToken', response.accessToken);
            localStorage.setItem('refreshToken', response.refreshToken);
            resetActiveCompanyToUser(response.user);
            markMaestroLoginWelcome();
            markSessionActivity();
            set((state) => {
              state.loggedIn = true;
              state.user = response.user;
              state.mfaRequired = false;
              state.mfaChallenge = null;
            });
            
            // SECURITY: Use feature access from MFA response (already validated server-side)
            if (response.user.role !== 'SUPER_ADMIN' && response.featureAccess) {
              console.log('[AuthStore] ✅ Loading pre-validated feature access from MFA response');
              useFeatureAccessStore.getState().setFeatureAccess(
                response.featureAccess.features, 
                response.featureAccess.featuresMap
              );
            } else if (response.user.role !== 'SUPER_ADMIN') {
              // Fallback: Load feature access if not included in response (backward compatibility)
              console.log('[AuthStore] ⚠️ Feature access not in MFA response, loading separately');
              useFeatureAccessStore.getState().loadFeatureAccess();
            }
          } else {
            throw new Error('Invalid MFA verification response');
          }
        } catch (error) {
          console.error('MFA verification failed:', error);
          throw error;
        }
      },
      clearMFA: () => {
        set((state) => {
          state.mfaRequired = false;
          state.mfaChallenge = null;
        });
      },
      refresh: async () => {
        try {
          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            console.log('[AuthStore] No refresh token found, skipping refresh');
            return; // Don't set loggedIn to false if there's no token
          }

          console.log('[AuthStore] Attempting token refresh...');
          const response = await handleRefreshToken(refreshToken);
          if (response && response.accessToken) {
            localStorage.setItem('accessToken', response.accessToken);
            localStorage.setItem('refreshToken', response.refreshToken);
            set((state) => {
              state.loggedIn = true;
              state.user = response.user;
            });
            console.log('[AuthStore] ✅ Token refresh successful');
          }
        } catch (error) {
          console.error('[AuthStore] ❌ Token refresh failed:', error);
          set((state) => {
            state.loggedIn = false;
            state.user = null;
          });
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      },
      initializeUser: async () => {
        try {
          const token = localStorage.getItem('accessToken');
          if (!token) {
            throw new Error('No access token found');
          }

          // Restore impersonation state from localStorage if it exists
          const impersonationDataStr = localStorage.getItem('impersonationData');
          if (impersonationDataStr) {
            try {
              const impersonationData = JSON.parse(impersonationDataStr);
              console.log('[AuthStore] Restoring impersonation state from localStorage');
              set((state) => {
                state.impersonation = impersonationData;
              });
            } catch (parseError) {
              console.error('Failed to parse impersonation data:', parseError);
              localStorage.removeItem('impersonationData');
            }
          }

          // Try to refresh the token first (silent — don't toast; getMe may still succeed)
          try {
            const refreshResponse = await handleRefreshToken(localStorage.getItem('refreshToken') || '', {
              silent: true,
            });
            if (refreshResponse?.accessToken) {
              localStorage.setItem('accessToken', refreshResponse.accessToken);
              localStorage.setItem('refreshToken', refreshResponse.refreshToken);
            }
          } catch (refreshError) {
            console.warn('Token refresh failed during initialization:', refreshError);
            // Continue with existing token if refresh fails
          }

          const user = await getMe();
          set((state) => {
            state.loggedIn = true;
            state.user = user;
          });
          
          // Load feature access after user is initialized
          if (user.role !== 'SUPER_ADMIN') {
            useFeatureAccessStore.getState().loadFeatureAccess();
          }
        } catch (error) {
          console.error('Failed to fetch logged-in user:', error);
          set((state) => {
            state.loggedIn = false;
            state.user = null;
          });
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
        }
      },
      logout: () => {
        set((state) => {
          state.loggedIn = false;
          state.user = null;
          state.mfaRequired = false;
          state.mfaChallenge = null;
          state.impersonation = null;
        });
        clearUserDataCache();
        clearMaestroPanelSession();
        clearStoredCompany();
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('impersonationData');
        clearSessionActivity();
        // Clear feature access on logout
        useFeatureAccessStore.getState().clearFeatureAccess();
      },
      startImpersonation: (impersonationData, accessToken, refreshToken) => {
        // Save original admin token before impersonating
        const originalAdminToken = localStorage.getItem('accessToken') || '';
        
        // Store impersonation data
        const fullImpersonationData: ImpersonationInfo = {
          ...impersonationData,
          originalAdminToken,
        };
        
        // Save to localStorage for persistence across page reloads
        localStorage.setItem('impersonationData', JSON.stringify(fullImpersonationData));
        clearUserDataCache();
        
        // Switch to impersonated user's tokens
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        
        set((state) => {
          state.impersonation = fullImpersonationData;
          // User will be loaded via initializeUser
        });
      },
      stopImpersonation: async () => {
        const state = useAuthStore.getState();
        
        if (!state.impersonation) {
          console.warn('Not currently impersonating');
          return;
        }
        
        const { originalAdminToken } = state.impersonation;
        
        // Restore original admin token
        localStorage.setItem('accessToken', originalAdminToken);
        localStorage.removeItem('refreshToken'); // Admin will need to refresh
        localStorage.removeItem('impersonationData');
        clearUserDataCache();
        
        set((state) => {
          state.impersonation = null;
        });
        
        // Reload admin user data
        try {
          await state.initializeUser();
          // Reload the page to reset all state
          window.location.href = '/admin/customers';
        } catch (error) {
          console.error('Failed to restore admin session:', error);
          // Force logout and redirect to login
          state.logout();
          window.location.href = '/sign-in';
        }
      },
    }))
  )
);
